import { bound } from "@frui.ts/helpers/dist";
import type { AuthorizationRequestHandler, StringMap, LocationLike } from "@openid/appauth";
import {
  AuthorizationRequest,
  AuthorizationNotifier,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  TokenRequest,
  TokenResponse,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  FetchRequestor,
  LocalStorageBackend,
  BasicQueryStringUtils,
  RedirectRequestHandler,
} from "@openid/appauth";
import type { AuthorizationRequestJson } from "@openid/appauth/src/authorization_request";
import { createDecoder } from "fast-jwt";
import castArray from "lodash/castArray";
import { computed, observable, toJS } from "mobx";
import type EventBus from "services/eventBus";
import { userLogout } from "services/eventBus";
import type { EntityNames } from "../config";
import { accessTokenKey, clientSecret, refreshTokenKey } from "../config";
import type CurrentUser from "../entities/currentUser";
import { openIdConnectUrl, clientId, redirectUri, scope } from "../config";
import type SessionRepository from "../repositories/sessionRepository";
import type NewVersionService from "./newVersionService";

export interface IdToken {
  role?: string[] | string;
  sub: string;
  family_name: string;
  given_name: string;
  name: string;
  preferred_username: string;
  [key: string]: any;
}

export type RightsObject =
  | "callcenter"
  | "callcenter.finance"
  | "callcenter.chargeauditing"
  | "distributors"
  | "distributors.users"
  | "distributors.reports"
  | "enforcement"
  | "enforcement.reports"
  | "enforcement.callcenter"
  | "enforcement.lustration"
  | "enums"
  | "finance"
  | "finance.reports"
  | "finance.factualauditing"
  | "finance.commandoperating"
  | "finance.chiefaccounting"
  | "finance.accountingofficer"
  | "charges"
  | "charges.reports"
  | "charges.emailblacklist"
  | "infrastructure"
  | "kiosks"
  | "kiosks.reports"
  | "reports"
  | "system.dlt"
  | "users";

export interface AccessToken {
  role?: string[] | string;
  [key: string]: any;
}

export type OperationType = "read" | "write";

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  // tslint:disable-next-line:bool-param-default
  parse(input: LocationLike, useHash?: boolean): StringMap {
    return super.parse(input, false /* never use hash */);
  }
}

export default class SecurityService {
  static REFRESH_INTERVAL_BUFFER = 60;
  private notifier: AuthorizationNotifier = new AuthorizationNotifier();
  private authorizationHandler: AuthorizationRequestHandler = new RedirectRequestHandler(
    new LocalStorageBackend(),
    new NoHashQueryStringUtils()
  );
  private requestor: FetchRequestor = new FetchRequestor();
  private tokenHandler: BaseTokenRequestHandler;
  private configuration?: AuthorizationServiceConfiguration;
  private jwtDecoder = createDecoder();
  @observable private idToken?: IdToken;
  private originalIdToken?: string;
  @observable private accessTokenResponse?: TokenResponse;
  private refreshToken?: string;
  @observable logoutUrl?: string;
  @observable loginInProgress: boolean;

  constructor(private repository: SessionRepository, private eventBus: EventBus, private newVersionService: NewVersionService) {
    this.tokenHandler = new BaseTokenRequestHandler(this.requestor);
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    this.eventBus.subscribe(userLogout, this.logout);

    this.notifier.setAuthorizationListener((request, response, error) => {
      if (response) {
        let codeVerifier: string | undefined;
        if (request.internal?.code_verifier) {
          codeVerifier = request.internal.code_verifier;
        }

        this.accessTokenRequest(response.code, codeVerifier);
      }
    });
  }

  async completeLogin() {
    this.loginInProgress = true;
    await this.authorizationHandler.completeAuthorizationRequestIfPossible();
  }

  get accessToken() {
    return this.accessTokenResponse?.accessToken ?? "";
  }

  async initialize() {
    this.configuration = await this.fetchServiceConfiguration();
    const refreshToken = sessionStorage.getItem(refreshTokenKey);

    if (sessionStorage.getItem(accessTokenKey) && refreshToken) {
      await this.refreshTokenRequest(refreshToken);
    }
  }

  fetchServiceConfiguration(): Promise<AuthorizationServiceConfiguration> {
    return AuthorizationServiceConfiguration.fetchFromIssuer(openIdConnectUrl, this.requestor);
  }

  login(identitifyProvider?: "idp:CzechPoint" | "idp:CendisLdap") {
    if (!this.configuration) {
      throw new Error("You cannot use login method before configuration initialization");
    }

    this.logoutUrl = undefined;

    const params: AuthorizationRequestJson = {
      client_id: clientId,
      redirect_uri: redirectUri,

      scope: `${scope} authadm.api offline_access openid profile email`,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: undefined,
      extras: { access_type: "offline" },
    };

    if (identitifyProvider && params.extras) {
      params.extras.acr_values = identitifyProvider;
    }

    const request = new AuthorizationRequest(params);

    this.authorizationHandler.performAuthorizationRequest(this.configuration, request);
  }

  @bound
  async accessTokenRequest(code: string, codeVerifier?: string): Promise<void> {
    if (!this.configuration) {
      throw new Error("Missing configuration");
    }

    const extras: StringMap = {
      client_secret: clientSecret,
    };

    if (codeVerifier) {
      extras.code_verifier = codeVerifier;
    }

    const request = new TokenRequest({
      client_id: clientId,
      redirect_uri: redirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code: code,
      refresh_token: undefined,
      extras,
    });

    return this.tokenHandler
      .performTokenRequest(this.configuration, request)
      .then(response => {
        this.accessTokenResponse = response;
        sessionStorage.setItem(accessTokenKey, this.accessTokenResponse.accessToken);

        this.refreshToken = response.refreshToken;
        if (this.refreshToken) {
          sessionStorage.setItem(refreshTokenKey, this.refreshToken);
        }

        if (response.idToken) {
          this.originalIdToken = response.idToken;
          this.idToken = this.jwtDecoder(response.idToken) as IdToken;
        }

        if (response.expiresIn) {
          setTimeout(this.refreshTokenRequest, (response.expiresIn - SecurityService.REFRESH_INTERVAL_BUFFER) * 1000);
        }
      })
      .finally(() => {
        this.loginInProgress = false;
        this.newVersionService.updateAppIfNewVersionIsAvailable();
      });
  }

  @bound
  async refreshTokenRequest(refreshToken?: string) {
    if (!this.configuration || (!this.refreshToken && !refreshToken)) {
      throw new Error("You cannot make refresh token request without valid token");
    }

    const extras: StringMap = {
      client_secret: clientSecret,
    };

    const request = new TokenRequest({
      client_id: clientId,
      redirect_uri: redirectUri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      refresh_token: refreshToken ?? this.refreshToken,
      extras: extras,
    });

    return this.tokenHandler
      .performTokenRequest(this.configuration, request)
      .then(response => {
        this.accessTokenResponse = response;
        sessionStorage.setItem(accessTokenKey, this.accessTokenResponse.accessToken);

        if (response.idToken) {
          this.originalIdToken = response.idToken;
          this.idToken = this.jwtDecoder(response.idToken) as IdToken;
        }

        this.refreshToken = response.refreshToken;
        if (this.refreshToken) {
          sessionStorage.setItem(refreshTokenKey, this.refreshToken);
        }

        if (response.expiresIn) {
          setTimeout(this.refreshTokenRequest, (response.expiresIn - SecurityService.REFRESH_INTERVAL_BUFFER) * 1000);
        }
      })
      .catch(() => {
        this.eventBus.publish(userLogout());
      });
  }

  @computed
  get user(): undefined | CurrentUser {
    if (this.idToken) {
      return {
        id: this.idToken.sub,
        username: this.idToken.preferred_username,
        name: this.idToken.name,
      };
    } else {
      return undefined;
    }
  }

  @computed
  get isLogged(): boolean {
    return !!this.accessTokenResponse && this.accessTokenResponse.isValid(0);
  }

  @bound
  logout() {
    if (this.originalIdToken) {
      this.logoutUrl = this.repository.logoutUrl(this.originalIdToken, redirectUri);
    }
  }

  onLogoutComplete() {
    this.accessTokenResponse = undefined;
    this.logoutUrl = undefined;
    sessionStorage.removeItem(accessTokenKey);
    sessionStorage.removeItem(refreshTokenKey);
  }

  allowTo(object: RightsObject, operation: OperationType = "read") {
    const token = this.idToken;

    if (!token || !token.role) {
      return false;
    }

    const roles = castArray(toJS(token.role));

    const allowedOperations = roles
      .filter(role => role.includes(object) && role.includes("backoffice"))
      .map(role => role.replace("backoffice.", "").replace(`${object}.`, "")) as Array<"r" | "rw">;

    if (operation === "read") {
      return allowedOperations.includes("r") || allowedOperations.includes("rw");
    } else {
      return allowedOperations.includes("rw");
    }
  }

  allowToEntity(entity: EntityNames, operation: OperationType = "read") {
    switch (entity) {
      case "bankExport":
      case "bankAccountBlacklist":
      case "creditExposure":
      case "order":
      case "internalTransfer":
      case "customerRefundCandidate":
        return this.allowTo("finance", operation);
      case "payGateTransaction":
        return this.allowTo("finance.reports", operation);
      case "chargeType":
      case "country":
      case "municipality":
      case "road":
      case "region":
        return this.allowTo("enums", operation);
      case "gateway":
      case "enforcementUser":
      case "enforcementTablet":
        return this.allowTo("enforcement", operation);
      case "lustrationReport":
        return this.allowTo("enforcement.lustration", operation);
      case "distributor":
        return this.allowTo("distributors", operation);
      case "distributorPaymentRequest":
        return this.allowTo("distributors.reports", operation);
      case "enum":
      case "systemComponent":
      case "bankAccountEnum":
      case "transferRefundPaymentTemplate":
      case "transferRefundForRefundOfRefundPaymentTemplate":
      case "payGateRefundPaymentTemplate":
      case "payGateToTransferRefundPaymentTemplate":
        return this.allowTo("enums", operation);
      case "outage":
        return this.allowTo("infrastructure", operation);
      case "emailBlacklist":
        return this.allowTo("charges.emailblacklist", operation);
      case "deadLetterTopicsSearch":
        return this.allowTo("system.dlt", operation);
      case "deadLetterTopicsActionSearch":
        return this.allowTo("system.dlt", operation);
      case "user":
      case "role":
      case "userRole":
      case "permissionArea":
      case "permissionAreaWithFaultyCombinationsResponse":
        return this.allowTo("users", operation);
      case "kiosk":
      case "kioskType":
        return this.allowTo("kiosks", operation);
      case "changeEmailRequest":
        return this.allowTo("callcenter", operation);
      case "chargeOrderSearch":
      case "unpairedPayment":
        return (
          this.allowTo("finance", operation) ||
          this.allowTo("callcenter", operation) ||
          this.allowTo("callcenter.finance", operation)
        );
      default:
        return false;
    }
  }
}
